// src/components/clinical/NoteTemplateSelector.jsx
// Modal for selecting a note template when creating a new clinical note.

import { useState, useEffect } from 'react';
import { X, Search, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiService } from '@/services/api';
import { cn } from '@/lib/utils';

const NOTE_TYPE_LABELS = {
  soap: 'SOAP',
  initial_evaluation: 'Initial Evaluation',
  progress_note: 'Progress Note',
  daily_note: 'Daily Note',
  discharge_summary: 'Discharge Summary',
  consultation: 'Consultation',
  procedure_note: 'Procedure Note',
  post_operative: 'Post-Operative',
  reassessment: 'Reassessment'
};

// Map encounter types to suggested note types
const ENCOUNTER_TYPE_MAP = {
  initial_evaluation: 'initial_evaluation',
  initial_visit: 'initial_evaluation',
  follow_up: 'progress_note',
  follow_up_visit: 'progress_note',
  progress: 'progress_note',
  daily: 'daily_note',
  discharge: 'discharge_summary',
  consultation: 'consultation',
  procedure: 'procedure_note',
  reassessment: 'reassessment'
};

const SPECIALTY_LABELS = {
  physical_therapy: 'Physical Therapy',
  occupational_therapy: 'Occupational Therapy',
  speech_language_pathology: 'Speech-Language Pathology',
  primary_care: 'Primary Care',
  orthopedics: 'Orthopedics',
  neurology: 'Neurology',
  cardiology: 'Cardiology',
  pain_management: 'Pain Management',
  psychiatry: 'Psychiatry',
  chiropractic: 'Chiropractic',
  sports_medicine: 'Sports Medicine',
  general: 'General',
};

export function NoteTemplateSelector({
  isOpen,
  onSelect,
  onCancel,
  encounterType,
  organizationId,
  userId,
  userSpecialty,
  disabledNoteTypes = {},
  inline = false,
  selectedTemplateId = null,
  allowedTemplateIds = null,
  templateNameFilter = null,
  excludeTemplateNames = null,
  includeNoteTypes = null,
  allowedNoteTypes = null,
  ignoreSpecialty: ignoreSpecialtyProp = false,
  // Preferred names (exact, case-insensitive). When any loaded template matches,
  // templates admitted only by includeNoteTypes are dropped so the picker narrows
  // to the name-filtered set. onPreferredMatched(bool) reports the outcome once
  // templates are loaded, so the caller can adjust its own copy.
  preferredTemplateNames = null,
  onPreferredMatched = null,
}) {
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);

  const suggestedNoteType = ENCOUNTER_TYPE_MAP[encounterType] || null;
  const enforcementActive = allowedNoteTypes?.length > 0;

  const preferredKey = (preferredTemplateNames || []).map(n => n.toLowerCase()).join('|');
  const preferredMatched = preferredKey.length > 0 && templates.some(t =>
    t.is_active !== false && preferredTemplateNames.some(name => t.name?.toLowerCase() === name.toLowerCase())
  );

  useEffect(() => {
    if (isLoading || !onPreferredMatched || !preferredKey) return;
    onPreferredMatched(preferredMatched);
  }, [isLoading, preferredMatched, preferredKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // enforcementActive is a dep: the allowlist typically arrives after mount
  // (case billing alerts load async), and the fetch shape depends on it
  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen, organizationId, userId, enforcementActive]);

  const fetchTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filters = {};
      // With a note-type allowlist active, load every template: the allowlist
      // filter decides what shows. Fetching only the encounter-type suggestion
      // (e.g. daily_note for a daily appointment) would leave no template of
      // the required type in the loaded list and render an empty picker.
      if (suggestedNoteType && !enforcementActive) {
        filters.note_type = suggestedNoteType;
      }
      const result = await apiService.getNoteTemplates(organizationId, userId, filters);
      setTemplates(result || []);

      // If we filtered by note_type and got no results, fetch all
      if (suggestedNoteType && (!result || result.length === 0)) {
        const allTemplates = await apiService.getNoteTemplates(organizationId, userId);
        setTemplates(allTemplates || []);
      }
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  };

  const matchesFilters = (t, { ignoreSpecialty = false } = {}) => {
    if (t.is_active === false) return false;
    if (allowedTemplateIds && !allowedTemplateIds.includes(t.id)) return false;
    // includeNoteTypes admits whole note types alongside the name filter,
    // except when a preferred-name template exists (then names only)
    const nameMatched = !!templateNameFilter && templateNameFilter.some(name => t.name?.toLowerCase() === name.toLowerCase());
    if (templateNameFilter && !nameMatched && (preferredMatched || !includeNoteTypes?.includes(t.note_type))) return false;
    if (excludeTemplateNames && excludeTemplateNames.some(name => t.name?.toLowerCase() === name.toLowerCase())) return false;
    // Strict: an untyped template is not "the required type" either
    if (enforcementActive && !allowedNoteTypes.includes(t.note_type)) return false;

    // Specialty filtering: check specialties[] first, fall back to specialty.
    // Name-matched templates (e.g. Case Note) are shown to every specialty.
    if (!ignoreSpecialty && !ignoreSpecialtyProp && !nameMatched && !showAllSpecialties && userSpecialty) {
      const hasSpecialties = t.specialties?.length > 0;
      if (hasSpecialties) {
        if (!t.specialties.includes(userSpecialty)) return false;
      } else if (t.specialty && t.specialty !== userSpecialty) {
        return false;
      }
    }

    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      t.name?.toLowerCase().includes(q) ||
      t.description?.toLowerCase().includes(q) ||
      t.note_type?.toLowerCase().includes(q) ||
      t.specialty?.toLowerCase().includes(q) ||
      t.specialties?.some(s => s.toLowerCase().includes(q))
    );
  };

  let filteredTemplates = templates.filter(t => matchesFilters(t));
  // With enforcement active there is no show-all escape hatch, so if the user's
  // specialty filter would hide every required-type template, widen to all
  // specialties rather than stranding the user with an empty list.
  if (enforcementActive && filteredTemplates.length === 0) {
    filteredTemplates = templates.filter(t => matchesFilters(t, { ignoreSpecialty: true }));
  }

  if (!isOpen) return null;

  const templateContent = (
    <>
      {/* Search & Filters */}
      <div className={cn(inline ? "space-y-2" : "p-4 border-b space-y-2")}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>
          {userSpecialty && !enforcementActive && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">
                Showing: {SPECIALTY_LABELS[userSpecialty] || userSpecialty} templates
              </span>
              <button
                onClick={() => setShowAllSpecialties(!showAllSpecialties)}
                className="text-xs text-primary hover:text-primary-dark"
              >
                {showAllSpecialties ? 'Show my specialty' : 'Show all specialties'}
              </button>
            </div>
          )}
          {enforcementActive && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-amber-600">
                Filtered to required note types
              </span>
            </div>
          )}
        </div>

      {/* Template List */}
      <div className={cn(inline ? "space-y-2 mt-3" : "flex-1 overflow-y-auto p-4 space-y-2")}>
          {/* Standard SOAP option - only shown when org has no custom templates and no allowlist is active */}
          {!isLoading && templates.length === 0 && !allowedTemplateIds && (
            <button
              onClick={() => onSelect(null)}
              className={cn(
                "w-full text-left p-4 border rounded-lg transition-colors",
                selectedTemplateId === null && inline
                  ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                  : "border-gray-200 hover:border-primary hover:bg-primary/5"
              )}
            >
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-gray-900">Standard SOAP Note</p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Classic Subjective, Objective, Assessment, Plan format with free-text sections.
                  </p>
                </div>
              </div>
            </button>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading templates...</span>
            </div>
          )}

          {error && (
            <div className="text-center py-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {!isLoading && [...filteredTemplates].sort((a, b) => {
            if (a.note_type === 'daily_note' && b.note_type !== 'daily_note') return -1;
            if (a.note_type !== 'daily_note' && b.note_type === 'daily_note') return 1;
            return 0;
          }).map(template => {
            const disabledReason = template.note_type && disabledNoteTypes[template.note_type];
            const isDisabled = !!disabledReason;

            return (
              <button
                key={template.id}
                onClick={() => !isDisabled && onSelect(template)}
                disabled={isDisabled}
                className={cn(
                  "w-full text-left p-4 border rounded-lg transition-colors",
                  isDisabled
                    ? "border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed"
                    : selectedTemplateId === template.id && inline
                    ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                    : "border-gray-200 hover:border-primary hover:bg-primary/5"
                )}
              >
                <div className="flex items-start gap-3">
                  <FileText className={cn("h-5 w-5 mt-0.5 flex-shrink-0", isDisabled ? "text-gray-300" : "text-primary")} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={cn("font-medium truncate", isDisabled ? "text-gray-400" : "text-gray-900")}>{template.name}</p>
                      {template.note_type && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 flex-shrink-0">
                          {NOTE_TYPE_LABELS[template.note_type] || template.note_type}
                        </span>
                      )}
                      {template.is_default && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 flex-shrink-0">
                          Default
                        </span>
                      )}
                    </div>
                    {isDisabled && (
                      <p className="text-xs text-amber-600 mt-0.5">{disabledReason}</p>
                    )}
                    {!isDisabled && template.description && (
                      <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{template.description}</p>
                    )}
                    {(template.specialties?.length > 0 || template.specialty) && (
                      <p className="text-xs text-gray-400 mt-1">
                        Specialty: {template.specialties?.length > 0
                          ? template.specialties.map(s => SPECIALTY_LABELS[s] || s).join(', ')
                          : (SPECIALTY_LABELS[template.specialty] || template.specialty)
                        }
                      </p>
                    )}
                  </div>
                </div>
              </button>
            );
          })}

          {!isLoading && !error && filteredTemplates.length === 0 && searchQuery && (
            <p className="text-center text-sm text-gray-500 py-4">
              No templates match your search.
            </p>
          )}
      </div>
    </>
  );

  if (inline) {
    return templateContent;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Select Note Template</h3>
          <button
            onClick={onCancel}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {templateContent}

        {/* Footer */}
        <div className="p-4 border-t">
          <Button variant="outline" onClick={onCancel} className="w-full">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
